import { Link as RouterLink, NavigateOptions } from '@tanstack/react-router';
import {
  buttonGhostIcon,
  buttonIcon,
  buttonStyles,
  ghostButton,
} from 'src/components/ui/Button';

import type { ReactNode } from 'react';
import type { ButtonProps } from 'src/components/ui/Button';

interface RouterButtonStyleProps {
  variant?: ButtonProps['variant'];
  size?: ButtonProps['size'];
  ghost?: ButtonProps['ghost'];
  borderless?: ButtonProps['borderless'];
  loading?: ButtonProps['loading'];
  IconLeft?: ButtonProps['IconLeft'];
  IconRight?: ButtonProps['IconRight'];
  className?: string;
}

export type RouterButtonProps = NavigateOptions & {
  buttonProps: RouterButtonStyleProps;
  children: ReactNode;
};

/**
 * A navigation-only button that renders as an <a> element.
 * Use this for links that look like buttons — do not use for form actions.
 * Resolves overlays-007: <button> must not be used for navigation.
 */
const RouterButton = ({
  buttonProps,
  children,
  ...props
}: RouterButtonProps) => {
  const {
    variant = 'primary',
    size = 'medium',
    ghost = false,
    borderless = false,
    loading = false,
    IconLeft = () => null,
    IconRight = () => null,
    className,
  } = buttonProps;

  const classes = ghost
    ? ghostButton({ variant, size, borderless, className })
    : buttonStyles({ variant, size, borderless, className });

  return (
    <RouterLink {...props} className={classes}>
      <>
        <IconLeft
          aria-hidden
          className={
            ghost
              ? buttonGhostIcon({ variant, size, loading })
              : buttonIcon({ variant, size, loading })
          }
        />
        {children}
        <IconRight
          aria-hidden
          className={
            ghost
              ? buttonGhostIcon({ variant, size })
              : buttonIcon({ variant, size })
          }
        />
      </>
    </RouterLink>
  );
};

export default RouterButton;
